/* eslint-disable @typescript-eslint/no-explicit-any */
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import {
    ComponentPropsWithoutRef,
    ComponentType,
    ElementRef,
    ReactNode,
    forwardRef,
    useEffect,
    useState,
} from "react";

import {
    Tooltip,
    TooltipContent,
    TooltipPortal,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/plate-ui/tooltip";

export function withTooltip<T extends ComponentType<any> | keyof HTMLElementTagNameMap>(
    Component: T
) {
    return forwardRef<
        ElementRef<T>,
        {
            tooltipContentProps?: Omit<
                ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>,
                "children"
            >;
            tooltipProps?: Omit<ComponentPropsWithoutRef<typeof TooltipPrimitive.Root>, "children">;
            tooltip?: ReactNode;
        } & ComponentPropsWithoutRef<T> &
            Omit<TooltipPrimitive.TooltipProviderProps, "children">
    >(function ExtendComponent(
        {
            delayDuration = 0,
            disableHoverableContent = true,
            skipDelayDuration = 0,
            tooltip,
            tooltipContentProps,
            tooltipProps,
            ...props
        },
        ref
    ) {
        const [mounted, setMounted] = useState(false);

        useEffect(() => {
            setMounted(true);
        }, []);

        /* eslint-disable @typescript-eslint/no-explicit-any */
        const component = <Component ref={ref} {...(props as any)} />;

        if (tooltip && mounted) {
            return (
                <TooltipProvider
                    delayDuration={delayDuration}
                    disableHoverableContent={disableHoverableContent}
                    skipDelayDuration={skipDelayDuration}
                >
                    <Tooltip {...tooltipProps}>
                        <TooltipTrigger asChild>{component}</TooltipTrigger>

                        <TooltipPortal>
                            <TooltipContent {...tooltipContentProps}>{tooltip}</TooltipContent>
                        </TooltipPortal>
                    </Tooltip>
                </TooltipProvider>
            );
        }

        return component;
    });
}
